// "BY CLIENT" — the same rows, folded client › retainer › job, with EVERY job in its place.
//
// The old screen lost charges in three ways this shape removes:
//   - a cost to charge on, on a job that does not bill yet, was drawn as a loose row above
//     the retainer's jobs, so looking under the job found nothing. Here it is a job node
//     like any other, marked as not billing yet, holding its own charge.
//   - a job was a second fold inside a retainer fold. Here a job's charges are always
//     drawn under it; nothing is two clicks deep.
//   - settled engagements vanished behind a button at the bottom of the page. Here the
//     toggle sits beside the view switch and states how many it hides.
//
// PURE GROUPING. No figure is computed; a node carries the server's own cards.

const jobNode = (card) => ({
  key: `job:${card.id}`,
  jobId: card.id,
  title: card.title,
  card,
  billsOwn: true,
  charges: card.charges,
  toRaise: card.expectedCharges,
  settled: !!card.settled,
});

const pendingNode = (p) => ({
  key: `job:${p.jobId}`,
  jobId: p.jobId,
  title: p.jobTitle,
  card: null,
  billsOwn: false,
  charges: [],
  toRaise: [],
  settled: false,
});

export const buildClientTree = (data) => {
  const clients = new Map();
  const clientOf = (id, name) => {
    const k = String(id);
    if (!clients.has(k)) clients.set(k, { key: `client:${k}`, clientId: id, clientName: name, retainers: [], oneOffs: [] });
    return clients.get(k);
  };

  const retainerNodes = new Map();
  for (const r of data.retainers) {
    const node = { key: `retainer:${r.id}`, card: r, jobs: [], settled: !!r.settled };
    retainerNodes.set(String(r.id), node);
    clientOf(r.clientId, r.clientName).retainers.push(node);
  }

  // Jobs and pending costs land under their retainer when it has a card, and directly
  // under their client otherwise — rendered loose rather than dropped, as before.
  const jobsHome = (retainerId, clientId, clientName) => {
    const rn = retainerId != null ? retainerNodes.get(String(retainerId)) : null;
    return rn ? rn.jobs : clientOf(clientId, clientName).oneOffs;
  };
  const byJob = new Map();
  for (const j of data.jobs) {
    const node = jobNode(j);
    byJob.set(String(j.id), node);
    jobsHome(j.retainerId, j.clientId, j.clientName).push(node);
  }
  for (const p of data.pendingConversions) {
    let node = byJob.get(String(p.jobId));
    if (!node) {
      node = pendingNode(p);
      byJob.set(String(p.jobId), node);
      jobsHome(p.retainerId, p.clientId, p.clientName).push(node);
    }
    node.toRaise = [...node.toRaise, p];
    node.settled = false;
  }

  return [...clients.values()]
    .map((c) => ({
      ...c,
      settled: c.retainers.every((r) => r.settled) && c.oneOffs.every((j) => j.settled),
    }))
    .sort((a, b) => String(a.clientName).localeCompare(String(b.clientName), 'he'));
};
